// /frontend/src/components/AccountDropdown.js  (v2.0 – modernized UI)
import React, { useState, useRef, useEffect } from 'react';

function AccountDropdown({ accounts, selectedId, onChange }) {
  const [open, setOpen]       = useState(false);
  const [hovered, setHovered] = useState(null);
  const dropdownRef           = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAll      = selectedId === 'all';
  const selectedAcc = accounts.find(a => a.id === selectedId);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>

      {/* Trigger button */}
      <button
        type='button'
        onClick={() => setOpen(!open)}
        style={{
          display      : 'flex',
          alignItems   : 'center',
          gap          : 7,
          padding      : '7px 12px',
          borderRadius : 8,
          border       : `1.5px solid ${open ? '#2563eb' : '#e5e7eb'}`,
          background   : open ? '#eff6ff' : '#ffffff',
          cursor       : 'pointer',
          fontSize     : 13,
          fontWeight   : 600,
          color        : open ? '#2563eb' : '#374151',
          transition   : 'all 0.15s',
          whiteSpace   : 'nowrap',
          boxShadow    : '0 1px 2px rgba(0,0,0,0.05)',
          maxWidth     : 200,
        }}
      >
        {selectedAcc ? (
          <>
            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {selectedAcc.avatar ? (
                <img
                  src={selectedAcc.avatar}
                  alt=''
                  style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #e5e7eb' }}
                />
              ) : (
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className='fas fa-user' style={{ fontSize: 9, color: '#9ca3af' }} />
                </div>
              )}
              {selectedAcc.icon && (
                <i className={selectedAcc.icon} style={{
                  position: 'absolute', bottom: -2, right: -3,
                  fontSize: 9, color: '#374151',
                  textShadow: '0 0 0 2px #fff',
                }} />
              )}
            </div>
            <span style={{
              maxWidth    : 110,
              overflow    : 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace  : 'nowrap',
            }}>
              {selectedAcc.username}
            </span>
          </>
        ) : (
          <>
            <div style={{
              width: 22, height: 22, borderRadius: '50%',
              background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <i className='fas fa-users' style={{ fontSize: 9, color: '#2563eb' }} />
            </div>
            All Accounts
          </>
        )}
        <i
          className={`fas fa-chevron-${open ? 'up' : 'down'}`}
          style={{ fontSize: 10, color: '#9ca3af', marginLeft: 2, flexShrink: 0 }}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position    : 'absolute',
          top         : 'calc(100% + 6px)',
          left        : 0,
          background  : '#ffffff',
          border      : '1px solid #e5e7eb',
          borderRadius: 10,
          minWidth    : 220,
          maxHeight   : 280,
          overflowY   : 'auto',
          zIndex      : 1000,
          padding     : 4,
          boxShadow   : '0 8px 24px rgba(0,0,0,0.1)',
        }}>

          {/* All accounts option */}
          <div
            onClick={() => { onChange('all'); setOpen(false); }}
            onMouseEnter={() => setHovered('all')}
            onMouseLeave={() => setHovered(null)}
            style={{
              display      : 'flex',
              alignItems   : 'center',
              gap          : 10,
              padding      : '8px 12px',
              borderRadius : 7,
              cursor       : 'pointer',
              background   : hovered === 'all'
                ? '#f8fafc'
                : isAll
                  ? '#eff6ff'
                  : 'transparent',
              marginBottom : 2,
            }}
          >
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <i className='fas fa-users' style={{ fontSize: 12, color: '#2563eb' }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: isAll ? 700 : 500, color: isAll ? '#2563eb' : '#374151' }}>
              All Accounts
            </span>
            {isAll && <i className='fas fa-check' style={{ fontSize: 10, color: '#2563eb', marginLeft: 'auto' }} />}
          </div>

          {/* Divider */}
          {accounts.length > 0 && (
            <div style={{ height: 1, background: '#f3f4f6', margin: '4px 8px' }} />
          )}

          {/* Individual accounts */}
          {accounts.map(acc => {
            const isSelected = selectedId === acc.id;
            return (
              <div
                key={acc.id}
                onClick={() => { onChange(acc.id); setOpen(false); }}
                onMouseEnter={() => setHovered(acc.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  display      : 'flex',
                  alignItems   : 'center',
                  gap          : 10,
                  padding      : '8px 12px',
                  borderRadius : 7,
                  cursor       : 'pointer',
                  background   : hovered === acc.id
                    ? '#f8fafc'
                    : isSelected
                      ? '#eff6ff'
                      : 'transparent',
                  transition   : 'background 0.1s',
                }}
              >
                {/* Avatar with platform badge */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  {acc.avatar ? (
                    <img
                      src={acc.avatar}
                      alt=''
                      style={{
                        width: 32, height: 32, borderRadius: '50%', objectFit: 'cover',
                        border: `2px solid ${isSelected ? '#93c5fd' : '#e5e7eb'}`,
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `2px solid ${isSelected ? '#93c5fd' : '#e5e7eb'}`,
                    }}>
                      <i className='fas fa-user' style={{ fontSize: 11, color: '#9ca3af' }} />
                    </div>
                  )}
                  {acc.icon && (
                    <div style={{
                      position: 'absolute', bottom: -2, right: -2,
                      width: 14, height: 14, borderRadius: '50%',
                      background: '#fff', border: '1.5px solid #e5e7eb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className={acc.icon} style={{ fontSize: 7, color: '#374151' }} />
                    </div>
                  )}
                </div>

                {/* Username */}
                <span style={{
                  fontSize    : 13,
                  fontWeight  : isSelected ? 700 : 500,
                  color       : isSelected ? '#2563eb' : '#374151',
                  maxWidth    : 130,
                  overflow    : 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace  : 'nowrap',
                }}>
                  {acc.username || 'Unknown'}
                </span>

                {isSelected && (
                  <i className='fas fa-check' style={{ fontSize: 10, color: '#2563eb', marginLeft: 'auto' }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AccountDropdown;